// hooks/useProductVariations.js
import { useState, useEffect, useMemo } from "react";
import { getVariations } from "../services/Products/ProductServices";

/**
 * Custom hook để quản lý variations của product
 * @param {number} productId - ID của sản phẩm
 * @param {Array} productAttributes - Attributes của sản phẩm từ API chính
 * @returns {Object} - Object chứa variations data và các helper functions
 */
export const useProductVariations = (productId) => {
  const [variations, setVariations] = useState([]);
  const [isLoadingVariations, setIsLoadingVariations] = useState(false);
  const [variationsError, setVariationsError] = useState(null);
  const [selectedVariation, setSelectedVariation] = useState(null);
  const [pendingSelection, setPendingSelection] = useState(null);

  // Fetch variations khi productId thay đổi
  useEffect(() => {
    if (!productId) return;

    // Reset states khi productId thay đổi
    setSelectedVariation(null);
    setPendingSelection(null);

    const fetchVariations = async () => {
      setIsLoadingVariations(true);
      setVariationsError(null);

      try {
        const variationsData = await getVariations(productId);
        setVariations(variationsData || []);

        // Không tự động chọn variation đầu tiên nữa
        // Để component cha quyết định variation nào sẽ được chọn
      } catch (error) {
        console.error("Error fetching variations:", error);
        setVariationsError(error.message || "Failed to fetch variations");
      } finally {
        setIsLoadingVariations(false);
      }
    };

    fetchVariations();
  }, [productId]);

  // Xử lý pending selection khi variations được load xong
  useEffect(() => {
    if (variations.length > 0 && pendingSelection && !selectedVariation) {
      const { optionValue, attributeName } = pendingSelection;
      const variation = findVariationByOption(optionValue, attributeName);
      
      if (variation) {
        setSelectedVariation(variation);
        setPendingSelection(null); // Clear pending selection
      }
    }
  }, [variations, pendingSelection, selectedVariation]);

  /**
   * Tìm variation theo option được chọn
   * @param {string} optionValue - Giá trị option được chọn (ví dụ: "Full Box", "No Box")
   * @param {string} attributeName - Tên attribute (mặc định là "Option")
   * @returns {Object|null} - Variation object hoặc null nếu không tìm thấy
   */
  const findVariationByOption = (optionValue, attributeName = "Option") => {
    if (!variations || variations.length === 0) return null;

    return variations.find((variation) => {
      const attribute = variation.attributes?.find(
        (attr) => 
          attr.name.toLowerCase() === attributeName.toLowerCase() ||
          attr.slug.toLowerCase() === attributeName.toLowerCase()
      );
      return attribute && attribute.option === optionValue;
    });
  };

  /**
   * Cập nhật variation được chọn dựa trên option
   * @param {string} optionValue - Giá trị option được chọn
   * @param {string} attributeName - Tên attribute
   */
  const selectVariationByOption = (optionValue, attributeName = "Option") => {

    // Nếu variations chưa được load xong, lưu lại để xử lý sau
    if (variations.length === 0 && isLoadingVariations) {
      setPendingSelection({ optionValue, attributeName });
      return;
    }

    const variation = findVariationByOption(optionValue, attributeName);
    
    if (variation) {
      setSelectedVariation(variation);
      setPendingSelection(null); // Clear any pending selection
    } else {
      console.warn(`No variation found for option "${optionValue}" in attribute "${attributeName}"`);
      // Nếu không tìm thấy và variations đã load xong, có thể đây là pending selection
      if (variations.length === 0) {
        setPendingSelection({ optionValue, attributeName });
      }
    }
  };

  /**
   * Lấy thông tin giá của variation hiện tại được chọn
   * @returns {Object} - Object chứa thông tin giá
   */
  const getCurrentPriceInfo = useMemo(() => {
    if (!selectedVariation) return null;

    return {
      id: selectedVariation.id,
      price: selectedVariation.price,
      regularPrice: selectedVariation.regular_price,
      salePrice: selectedVariation.sale_price,
      onSale: selectedVariation.on_sale,
      // Format giá cho hiển thị
      formattedPrice: formatPrice(selectedVariation.price),
      formattedRegularPrice: formatPrice(selectedVariation.regular_price),
      formattedSalePrice: formatPrice(selectedVariation.sale_price),
      // Tính % giảm giá nếu có
      discountPercentage: selectedVariation.on_sale
        ? calculateDiscountPercentage(
            selectedVariation.regular_price,
            selectedVariation.sale_price
          )
        : 0,
    };
  }, [selectedVariation]);

  /**
   * Lấy danh sách tất cả options available
   * @param {string} attributeName - Tên attribute cần lấy options
   * @returns {Array} - Mảng các option values
   */
  const getAvailableOptions = (attributeName = "Option") => {
    if (!variations || variations.length === 0) return [];

    return variations
      .map((variation) => {
        const attribute = variation.attributes?.find(
          (attr) => attr.name.toLowerCase() === attributeName.toLowerCase()
        );
        return attribute?.option;
      })
      .filter(Boolean); // Loại bỏ undefined/null values
  };

  /**
   * Lấy thông tin giá của tất cả variations để hiển thị
   * @returns {Array} - Mảng các object chứa option và giá tương ứng
   */
  const getAllVariationPrices = () => {
    if (!variations || variations.length === 0) return [];

    return variations.map((variation) => {
      const optionAttribute = variation.attributes?.find(
        (attr) => attr.name.toLowerCase() === "option"
      );

      return {
        id: variation.id,
        option: optionAttribute?.option || "",
        price: variation.price,
        regularPrice: variation.regular_price,
        salePrice: variation.sale_price,
        onSale: variation.on_sale,
        formattedPrice: formatPrice(variation.price),
        formattedRegularPrice: formatPrice(variation.regular_price),
        formattedSalePrice: formatPrice(variation.sale_price),
        discountPercentage: variation.on_sale
          ? calculateDiscountPercentage(
              variation.regular_price,
              variation.sale_price
            )
          : 0,
      };
    });
  };

  return {
    // Data
    variations,
    selectedVariation,
    currentPriceInfo: getCurrentPriceInfo,

    // Loading states
    isLoadingVariations,
    variationsError,

    // Actions
    selectVariationByOption,
    findVariationByOption,

    // Helpers
    getAvailableOptions,
    getAllVariationPrices,

    // Computed values
    hasVariations: variations && variations.length > 0,
    variationsCount: variations?.length || 0,
  };
};

/**
 * Format giá tiền với separators
 * @param {string|number} price - Giá cần format
 * @returns {string} - Giá đã được format
 */
const formatPrice = (price) => {
  if (!price) return "";
  const numPrice = parseFloat(price);
  return `${numPrice.toLocaleString("vi-VN")}₫`;
};

/**
 * Tính phần trăm giảm giá
 * @param {string|number} regularPrice - Giá gốc
 * @param {string|number} salePrice - Giá sale
 * @returns {number} - Phần trăm giảm giá
 */
const calculateDiscountPercentage = (regularPrice, salePrice) => {
  const regular = parseFloat(regularPrice);
  const sale = parseFloat(salePrice);

  if (!regular || !sale || sale >= regular) return 0;

  return Math.round(((regular - sale) / regular) * 100);
};

export default useProductVariations;
